import { useEffect, useRef, useState } from "react";

export function useMicThresholdTrigger({
  threshold = 0.05,
  onTrigger,
  autoStart = false,
}) {
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [isListening, setIsListening] = useState(false);

  const calculateVolume = (dataArray) => {
    let sumSquares = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const normalized = (dataArray[i] - 128) / 128;
      sumSquares += normalized * normalized;
    }
    return Math.sqrt(sumSquares / dataArray.length);
  };

  const listen = async () => {
    audioContextRef.current = new AudioContext();
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;

    const source = audioContextRef.current.createMediaStreamSource(stream);
    analyserRef.current = audioContextRef.current.createAnalyser();
    analyserRef.current.fftSize = 2048;
    source.connect(analyserRef.current);

    const dataArray = new Uint8Array(analyserRef.current.fftSize);

    const check = () => {
      analyserRef.current?.getByteTimeDomainData(dataArray);
      const volume = calculateVolume(dataArray);
      if (volume > threshold) {
        stop(); // clean up
        if (onTrigger) onTrigger();
      } else {
        animationFrameRef.current = requestAnimationFrame(check);
      }
    };

    setIsListening(true);
    check();
  };

  const stop = () => {
    setIsListening(false);
    cancelAnimationFrame(animationFrameRef.current);
    streamRef.current?.getTracks().forEach((track) => track.stop());
    audioContextRef.current?.close();

    // cleanup
    streamRef.current = null;
    audioContextRef.current = null;
    analyserRef.current = null;
  };

  useEffect(() => {
    if (autoStart) {
      setTimeout(() => {
        listen();
      }, 0);
    }
    return () => {
      stop();
    };
  }, [autoStart]);

  return {
    start: listen,
    stop,
    isListening,
  };
}
